import { Spinner } from '@quadminds/ui-kit';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useDarkMode } from 'usehooks-ts';

import {
    MAIN_PLATFORM_BG_CLASS,
    STORK_BG_CLASS,
    MINDY_BG_CLASS,
    FLEETCARE_BG_CLASS,
    OMS_BG_CLASS,
} from './backgrounds';
import logo_claro from '../assets/logo_claro.svg';
import logo_fleetcare from '../assets/logo_fleetcare.svg';
import logo_mindy from '../assets/logo_mindy.svg';
import logo_oms from '../assets/logo_oms.svg';
import logo_qm_flash from '../assets/logo_qm_flash.svg';
import logo_qm_flash_white from '../assets/logo_qm_flash_white.svg';
import logo_qm_saas from '../assets/logo_qm_saas.svg';
import logo_qm_saas_white from '../assets/logo_qm_saas_white.svg';
import logo_quadmindsAI from '../assets/logo_quadmindsAI.svg';
import logo_stork_day from '../assets/logo_tdc.svg';
import logo_stork_night from '../assets/logo_tdc_night.svg';
import useCheckMobileScreen from '../hooks/useCheckMobileScreen';
import {
    applyBrandFavicon,
    hasClaroParam,
    isBrandedPlatform,
    isCorePlatform,
    isFleetcare,
    isFlash,
    isMindy,
    isOms,
    isSaas,
    isStork,
    pickPlatformValue,
} from '../utils';

// Resolves the platform logo. `preferDayLogo` selects the light background variant
// for the platforms that ship both a day and a night logo.
const resolveLogo = (preferDayLogo: boolean): string | undefined => {
    switch (true) {
        case isMindy:
            return logo_mindy;
        case isOms:
            return logo_oms;
        case isFleetcare:
            return logo_fleetcare;
        case isStork:
            return preferDayLogo ? logo_stork_day : logo_stork_night;
        case isFlash:
            return preferDayLogo ? logo_qm_flash : logo_qm_flash_white;
        case isSaas:
            return preferDayLogo ? logo_qm_saas : logo_qm_saas_white;
        default:
            return undefined;
    }
};

// Branded (mindy / fleetcare / oms) layout: full screen background + glass form panel
const BrandedLayout: React.FC<{ children: React.ReactNode; isLoading?: boolean; showClaro: boolean }> = ({
    children,
    isLoading,
    showClaro,
}) => (
    <motion.div
        className="min-h-screen relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
    >
        {/* Background - Full Screen */}
        <div
            className={clsx(
                'absolute inset-0 bg-cover bg-center',
                isMindy && MINDY_BG_CLASS,
                isFleetcare && FLEETCARE_BG_CLASS,
                isOms && OMS_BG_CLASS,
            )}
        ></div>

        {/* Right side - Form Container with Glass Effect */}
        <div className="relative ml-auto w-full lg:w-[480px] min-h-screen backdrop-blur-md lg:border-l border-gray-800">
            <div className="absolute inset-0 bg-[#1B1C1D] opacity-70 z-0"></div>
            {/* Logo Mindy - Top Left */}
            <motion.div
                className="absolute top-8 left-8 z-1"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
            >
                {isMindy && <img src={logo_mindy} alt="Mindy" className="h-8" />}
                {isFleetcare && <img src={logo_fleetcare} alt="Fleetcare" className="h-8" />}
                {isOms && <img src={logo_oms} alt="OMS" className="h-8" />}
            </motion.div>

            {/* Centered Content */}
            <div className="min-h-screen relative flex items-center justify-center px-12 py-20 z-1">
                <div className="w-full max-w-md">
                    <AnimatePresence mode="wait">
                        {isLoading ? (
                            <motion.div
                                key="spinner"
                                className="flex justify-center"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Spinner color="primary" size={60} />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="content"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                {children}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Footer logos - Bottom Right */}
            <motion.div
                className="absolute bottom-8 right-8 flex items-center gap-4 z-1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
            >
                {showClaro && <img src={logo_claro} alt="Claro" className="h-6 mb-1.5" />}
                <img src={logo_quadmindsAI} alt="QuadMinds AI" className="h-6" />
            </motion.div>
        </div>
    </motion.div>
);

const DefaultLayout: React.FC<{ children: React.ReactNode; isLoading?: boolean }> = ({ children, isLoading }) => {
    const { t } = useTranslation();
    const isMobile = useCheckMobileScreen();
    const isDarkMode = useDarkMode({ defaultValue: new Date().getHours() >= 18, initializeWithValue: false });
    const [searchParams] = useSearchParams();
    const showClaro = hasClaroParam(searchParams);

    const backgroundClasses = clsx(
        'w-screen min-h-screen flex flex-col justify-center items-center',
        'bg-cover bg-bottom',
        (isCorePlatform || !isStork) && !isBrandedPlatform && MAIN_PLATFORM_BG_CLASS,
        isStork && STORK_BG_CLASS,
        isMindy && MINDY_BG_CLASS,
        isFleetcare && FLEETCARE_BG_CLASS,
        isOms && OMS_BG_CLASS,
    );

    const companyLinkClasses = clsx(
        `no-underline dark:text-white mr-4`,
        pickPlatformValue({ branded: 'text-primary-600', stork: 'text-variant-700', fallback: 'text-blue-500' }),
    );

    const LOGO_TO_RENDER = resolveLogo(isMobile || !isDarkMode);

    useEffect(() => {
        applyBrandFavicon();
    }, []);

    if (isBrandedPlatform) {
        return (
            <BrandedLayout isLoading={isLoading} showClaro={showClaro}>
                {children}
            </BrandedLayout>
        );
    }

    return (
        <div className={`${isDarkMode ? 'dark' : ''}`}>
            <motion.section
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className={backgroundClasses}
            >
                <motion.img
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="my-5 object-cover 2xl:w-[14%] h-auto"
                    src={LOGO_TO_RENDER}
                    alt="Logo"
                />
                <AnimatePresence mode="wait">
                    {isLoading ? (
                        <motion.div
                            key="spinner"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Spinner color={isDarkMode ? 'primary' : 'default'} size={60} />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="content"
                            className="w-full flex flex-col justify-center items-center mb-10 z-10"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            {children}
                        </motion.div>
                    )}
                </AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="hidden py-2 sm:block fixed bottom-4"
                >
                    <motion.a
                        whileHover={{ scale: 1.05 }}
                        href="https://www.quadminds.com/contacto-plataforma-logistica-quadminds/"
                        target="_blank"
                        className={companyLinkClasses}
                        rel="noopener noreferrer"
                    >
                        <span>
                            © QuadMinds Technologies <span className="ml-2">·</span>
                        </span>
                    </motion.a>
                    <motion.a
                        whileHover={{ scale: 1.05 }}
                        href="https://www.quadminds.com/contacto-plataforma-logistica-quadminds/"
                        target="_blank"
                        className={companyLinkClasses}
                        rel="noopener noreferrer"
                    >
                        <span>
                            {t('default.contact')} <span className="ml-2">·</span>
                        </span>
                    </motion.a>
                    <motion.a
                        whileHover={{ scale: 1.05 }}
                        href="https://www.quadminds.com/terminos-y-condiciones-plataforma-quadminds/"
                        target="_blank"
                        className={companyLinkClasses}
                        rel="noopener noreferrer"
                    >
                        <span>{t('default.termsAndConds')}</span>
                    </motion.a>
                </motion.div>
            </motion.section>
        </div>
    );
};

export default DefaultLayout;
